import React, { useState, useEffect } from "react";
import {
  Button,
  DatePicker,
  Space,
  ConfigProvider,
  Tooltip,
  Typography,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useTranslate } from "#src/hooks";
import styled from "styled-components";
import { useAntTheme } from "hooks/useAntTheme/";
import { CalendarDotsIcon } from "@phosphor-icons/react";

const { RangePicker } = DatePicker;

export type PresetDateRangePickerType =
  | "custom"
  | "today"
  | "yesterday"
  | "7D"
  | "30D"
  | "2M"
  | "6M"
  | "12M"
  | "XTD";

const StyledRangePicker = styled(RangePicker)`
  .ant-picker-input:not(:first-child) {
    align-items: center;
  }
`;

interface PresetDateRangePickerProps {
  // Missing parts fall back to the default range.
  value?: {
    type: PresetDateRangePickerType | null;
    startDate: Dayjs | null;
    endDate: Dayjs | null;
  };
  onChange?: (range: {
    type: PresetDateRangePickerType;
    startDate: Dayjs;
    endDate: Dayjs;
  }) => void;
  defaultType?: PresetDateRangePickerType;
  // maxRangeInMonths?: number;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  dateFormat?: string;
  allowClear?: boolean;
  placeholder?: [string, string];
  disabled?: boolean;
}

export const PresetDateRangePicker: React.FC<PresetDateRangePickerProps> = ({
  value,
  onChange,
  defaultType = "today",
  minDate,
  maxDate,
  dateFormat = "YYYY-MM-DD",
  allowClear = true,
  placeholder,
  disabled,
}) => {
  const t = useTranslate();
  const { theme } = useAntTheme();

  const options: { label: string; value: PresetDateRangePickerType }[] = [
    {
      label: t("src.components.PresetDateRangePicker.custom"),
      value: "custom",
    },
    {
      label: t("src.components.PresetDateRangePicker.today"),
      value: "today",
    },
    {
      label: t("src.components.PresetDateRangePicker.yesterday"),
      value: "yesterday",
    },
    {
      label: t("src.components.PresetDateRangePicker.sevenDays"),
      value: "7D",
    },
    {
      label: t("src.components.PresetDateRangePicker.thirtyDays"),
      value: "30D",
    },
    {
      label: t("src.components.PresetDateRangePicker.twoMonths"),
      value: "2M",
    },
    {
      label: t("src.components.PresetDateRangePicker.twelveMonths"),
      value: "12M",
    },
  ];

  const selected = value?.type || defaultType;
  const range: [Dayjs, Dayjs] =
    value?.startDate && value?.endDate
      ? [value.startDate, value.endDate]
      : getRange(value?.type || defaultType);
  const [customOpen, setCustomOpen] = useState(false);

  useEffect(() => {
    if (!value?.startDate && !value?.endDate && onChange) {
      const [startDate, endDate] = getRange(defaultType);
      onChange({ type: defaultType, startDate, endDate });
    }
  }, [value?.startDate, value?.endDate, onChange, defaultType]);

  const handleSelect = (type: PresetDateRangePickerType) => {
    if (type === "custom") {
      setCustomOpen(true);
    } else {
      const [startDate, endDate] = getRange(type);
      setCustomOpen(false);
      onChange?.({ type, startDate, endDate });
    }
  };

  const handleCustomChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    const [startDate, endDate] = dates ?? [null, null];
    if (startDate && endDate) {
      setCustomOpen(false);
      onChange?.({ type: "custom", startDate, endDate });
    } else {
      const [startDate, endDate] = getRange(defaultType);
      setCustomOpen(false);
      onChange?.({ type: defaultType, startDate, endDate });
    }
  };

  return (
    <ConfigProvider
      theme={{
        components: {
          Button: {
            colorBorder: theme.colorBorder,
          },
        },
      }}
    >
      <Space.Compact>
        {options.map((opt) => {
          if (opt.value === "custom") {
            const showRangePicker = customOpen || selected === "custom";

            if (showRangePicker) {
              return (
                <StyledRangePicker
                  key="custom"
                  allowClear={allowClear}
                  value={range}
                  onChange={handleCustomChange}
                  format={dateFormat}
                  open={customOpen}
                  onOpenChange={(open) => setCustomOpen(open)}
                  type="primary"
                  placeholder={placeholder}
                  disabled={disabled}
                  minDate={minDate}
                  maxDate={maxDate}
                  suffixIcon={
                    <CalendarDotsIcon
                      size={20}
                      weight="duotone"
                      color={theme.colorPrimary}
                    />
                  }
                />
              );
            }

            return (
              <DatePicker
                key="custom"
                allowClear={false}
                value={range}
                onChange={handleCustomChange}
                format={() => t("src.components.PresetDateRangePicker.custom")}
                onOpenChange={(open) => open && setCustomOpen(true)}
                placeholder={placeholder?.[0]}
                disabled={disabled}
                suffixIcon={<CalendarDotsIcon size={20} weight="duotone" />}
              />
            );
          }

          return (
            <Tooltip
              key={opt.value}
              title={
                <Typography.Text style={{ color: "#fff", fontSize: 11 }}>
                  {!["today", "yesterday"].includes(opt.value) &&
                    `${getRange(opt.value)[0].format("LL")} - `}
                  {getRange(opt.value)[1].format("LL")}
                </Typography.Text>
              }
            >
              <Button
                type={selected === opt.value ? "primary" : "default"}
                onClick={() => handleSelect(opt.value)}
                disabled={disabled}
              >
                {opt.label}
              </Button>
            </Tooltip>
          );
        })}
      </Space.Compact>
    </ConfigProvider>
  );
};

export function getRange(
  type: PresetDateRangePickerType,
  now = dayjs()
): [Dayjs, Dayjs] {
  switch (type) {
    case "today":
      return [now.startOf("day"), now.endOf("day")];
    case "yesterday":
      return [
        now.subtract(1, "day").startOf("day"),
        now.subtract(1, "day").endOf("day"),
      ];
    case "7D":
      return [now.subtract(6, "day").startOf("day"), now.endOf("day")];
    case "30D":
      return [now.subtract(29, "day").startOf("day"), now.endOf("day")];
    case "2M":
      return [now.subtract(2, "month").startOf("day"), now.endOf("day")];
    case "6M":
      return [now.subtract(6, "month").startOf("day"), now.endOf("day")];
    case "12M":
      return [now.subtract(12, "month").startOf("day"), now.endOf("day")];
    case "XTD":
      return [now.startOf("year"), now.endOf("day")];
    default:
      return [now.startOf("day"), now.endOf("day")];
  }
}
